import React, { useState } from 'react';
import { authFetch } from '../utils/cloudFunctionsAuth';

const PDF_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/generateInvoicePdfHttp';

interface InvoicePdfActionsProps {
  invoiceId: string;
  invoiceNumber?: string;
  useNativePdf?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function InvoicePdfActions({
  invoiceId,
  invoiceNumber,
  disabled = false,
  disabledMessage,
  size = 'sm',
  className = '',
}: InvoicePdfActionsProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filename = invoiceNumber
    ? `${String(invoiceNumber).replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`
    : 'invoice.pdf';

  const fetchPdfBlob = async (): Promise<Blob> => {
    const resp = await authFetch(PDF_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceId }),
    });
    
    console.log('[InvoicePdfActions] Response status:', resp.status, 'Content-Type:', resp.headers.get('Content-Type'));
    
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({} as any));
      throw new Error(data?.error || `Failed to download invoice (HTTP ${resp.status})`);
    }
    
    const blob = await resp.blob();
    console.log('[InvoicePdfActions] Blob size:', blob.size, 'type:', blob.type);
    
    // Validate we received PDF data
    if (blob.size < 100) {
      console.error('[InvoicePdfActions] Received suspiciously small response:', blob.size, 'bytes');
    }
    
    // Check first bytes for PDF signature
    const firstBytes = await blob.slice(0, 4).text();
    console.log('[InvoicePdfActions] First 4 bytes:', firstBytes, firstBytes === '%PDF' ? '(valid PDF)' : '(NOT PDF!)');
    
    if (firstBytes !== '%PDF') {
      // Try to read as text to see what we got
      const fullText = await blob.text();
      console.error('[InvoicePdfActions] Received non-PDF data:', fullText.substring(0, 200));
      throw new Error('Server returned invalid PDF data');
    }
    
    return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
  };

  const handleView = async () => {
    if (disabled) return;
    setLoading(true);
    setError(null);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setError(e?.message || 'Failed to open invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (disabled) return;
    setLoading(true);
    setError(null);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || 'Failed to download invoice');
    } finally {
      setLoading(false);
    }
  };

  const btnSize = size === 'sm' ? 'btn-sm' : '';

  return (
    <div className={`d-flex flex-column gap-2 ${className}`}>
      {error && (
        <div className="alert alert-danger py-2 mb-0 d-flex align-items-center" style={{ fontSize: '0.8125rem' }}>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
      )}
      <div className="d-flex flex-wrap gap-2">
        <button
          type="button"
          className={`btn ${btnSize} d-flex align-items-center gap-2`}
          style={{
            background: 'var(--bs-tertiary-bg)',
            color: 'var(--bs-body-color)',
            border: '1px solid var(--bs-border-color)',
          }}
          onClick={handleView}
          disabled={loading || disabled}
          title={disabled ? disabledMessage : 'View PDF in new tab'}
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <i className="bi bi-eye"></i>
          )}
          View PDF
        </button>
        <button
          type="button"
          className={`btn ${btnSize} btn-primary d-flex align-items-center gap-2`}
          onClick={handleDownload}
          disabled={loading || disabled}
          title={disabled ? disabledMessage : 'Download PDF'}
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <i className="bi bi-download"></i>
          )}
          Download PDF
        </button>
      </div>
    </div>
  );
}

export default InvoicePdfActions;
